import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAdminAuth } from '../../../hooks/admin/useAdminAuth';

interface PortvilleApplication {
  id: string;
  name: string;
  organization: string;
  status: 'pending' | 'checked' | 'approved' | 'rejected';
  createdAt: any;
}

interface RecentPortvilleApplicationsProps {
  applications: PortvilleApplication[];
}

const RecentPortvilleApplications: React.FC<RecentPortvilleApplicationsProps> = ({ applications }) => {
  const navigate = useNavigate();
  const { hasPermission, userLevel } = useAdminAuth();

  const handleApplicationClick = (applicationId: string) => {
    // 최고관리자이거나 portville 권한이 있는 경우
    if (userLevel === 1 || hasPermission('portville')) {
      navigate(`/admin/portville/applications/${applicationId}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'checked':
        return 'text-blue-600 bg-blue-100';
      case 'approved':
        return 'text-green-600 bg-green-100';
      case 'rejected':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">최근 포트빌 신청</h2>
      <div className="space-y-4">
        {applications.length === 0 ? (
          <p className="text-gray-500 text-sm">최근 신청 내역이 없습니다.</p>
        ) : (
          applications.map((application) => (
            <div
              key={application.id}
              className={`border-b border-gray-200 pb-4 last:border-0 p-2 rounded-lg transition-colors ${
                (userLevel === 1 || hasPermission('portville')) ? 'cursor-pointer hover:bg-gray-50' : ''
              }`}
              onClick={() => handleApplicationClick(application.id)}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0 pr-1">
                  <p className="font-medium text-gray-900 truncate">{application.name}</p>
                  <p className="text-sm text-gray-500 truncate">{application.organization}</p>
                  <p className="text-sm text-gray-500">
                    {format(application.createdAt.toDate(), 'yyyy년 M월 d일')}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                      application.status
                    )}`}
                  >
                    {application.status === 'pending' && '대기중'}
                    {application.status === 'checked' && '검토완료'}
                    {application.status === 'approved' && '승인됨'}
                    {application.status === 'rejected' && '거절됨'}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentPortvilleApplications; 
